import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteImage } from '@/contexts/SiteImagesContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { CalendarCheck, CalendarClock, Menu, LogOut, ArrowLeft, Stethoscope } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { label: 'I miei appuntamenti', href: '/area-dottore', icon: CalendarCheck },
  { label: 'La mia disponibilità', href: '/area-dottore/disponibilita', icon: CalendarClock },
];

function SidebarContent({ doctorName, logoImg, isActive, onItemClick, logout, navigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-[hsl(var(--border))]">
        <div className="flex flex-col items-start gap-2">
          {logoImg && <img src={logoImg} alt="Studio Medico Boscovich" className="h-7" />}
          <div>
            <p className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>Area Dottore</p>
            <p className="text-xs text-muted-foreground">{doctorName}</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {SIDEBAR_ITEMS.map(item => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onItemClick}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors duration-200 ${
                isActive(item.href)
                  ? 'bg-[#238A92] text-white'
                  : 'text-[#1F2A37] hover:bg-[hsl(var(--secondary))]'
              }`}
              data-testid={`doctor-nav-${item.href}`}
            >
              <Icon size={18} />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-[hsl(var(--border))] space-y-1">
        <Link
          to="/"
          className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg text-[#1F2A37] hover:bg-[hsl(var(--secondary))] transition-colors duration-200"
        >
          <ArrowLeft size={18} /> Torna al Sito
        </Link>
        <button
          onClick={() => { logout(); navigate('/'); }}
          className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors duration-200"
          data-testid="doctor-logout-button"
        >
          <LogOut size={18} /> Esci
        </button>
      </div>
    </div>
  );
}

export default function DoctorLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [doctor, setDoctor] = useState(null);
  const logoImg = useSiteImage('logo_header');

  useEffect(() => {
    let active = true;
    axios.get(`${API}/doctor/me`)
      .then(res => { if (active) setDoctor(res.data); })
      .catch(() => { /* fallback to user name */ });
    return () => { active = false; };
  }, []);

  const doctorName = doctor
    ? `Dott. ${doctor.nome} ${doctor.cognome}`
    : `Dott. ${user?.nome || ''} ${user?.cognome || ''}`.trim();

  const isActive = (href) => {
    if (href === '/area-dottore') return location.pathname === '/area-dottore';
    return location.pathname.startsWith(href);
  };

  const sidebarProps = {
    doctorName, logoImg, isActive,
    onItemClick: () => setMobileOpen(false),
    logout, navigate,
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex">
      <aside className="hidden lg:block w-64 bg-white border-r border-[hsl(var(--border))] min-h-screen fixed">
        <SidebarContent {...sidebarProps} />
      </aside>
      <div className="flex-1 lg:ml-64">
        <header className="bg-white border-b border-[hsl(var(--border))] px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <SidebarContent {...sidebarProps} />
              </SheetContent>
            </Sheet>
            <h1 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Stethoscope size={18} className="text-[#238A92]" />
              {SIDEBAR_ITEMS.find(i => isActive(i.href))?.label || 'Area Dottore'}
            </h1>
          </div>
          <div className="text-sm text-muted-foreground">{user?.email}</div>
        </header>
        <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
